import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { ConfirmDialog } from '../components/ConfirmDialog.jsx';
import { mockSave } from '../services/botConfigService.js';

export function FallbackPanel({ bot, updateBot, mockErrorOn, notify }) {
  const [text, setText] = useState(bot.fallbackMessage);
  const [handoff, setHandoff] = useState(bot.handoffEnabled);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const isEmpty = !bot.fallbackMessage;

  function onToggle() {
    if (handoff) setConfirmOff(true);
    else setHandoff(true);
  }

  function save() {
    if (!text || text.trim().length < 10) {
      setErrorMsg('Fallback message must be at least 10 characters.');
      return;
    }
    if (text.length > 300) {
      setErrorMsg('Fallback message must be under 300 characters.');
      return;
    }
    setErrorMsg(null);
    setStatus('saving');
    mockSave(mockErrorOn)
      .then(() => {
        updateBot({ fallbackMessage: text, handoffEnabled: handoff });
        setStatus('idle');
        notify('success', 'Fallback message saved');
      })
      .catch(() => setStatus('error'));
  }

  return (
    <div>
      <div className="panel-header">
        <h1>Fallback message</h1>
        <p>What your bot says when it doesn't know the answer, and whether it offers a human instead.</p>
      </div>
      {status === 'error' && <AlertBanner text="We couldn't save your changes. Try again." onRetry={save} />}
      <div className="card">
        {isEmpty && status === 'idle' && !text && (
          <div className="empty-state" style={{ paddingTop: 6 }}>
            <div className="empty-icon"><Icon.life s={22} /></div>
            <h3>Set what your bot says when it's stuck</h3>
            <p>Shown whenever the AI can't confidently answer a visitor's question.</p>
          </div>
        )}
        <div className={'field' + (errorMsg ? ' invalid' : '')}>
          <label htmlFor="fallback">
            Fallback message
            <span className={'counter' + (text.length > 300 ? ' danger' : '')}>{text.length} / 300</span>
          </label>
          <textarea
            id="fallback"
            rows={3}
            placeholder="e.g. I'm not totally sure about that — would you like me to connect you with our team?"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {errorMsg && <div className="error-text"><Icon.alert s={12} /> {errorMsg}</div>}
        </div>
        {text && (
          <div className="field">
            <div className="preview-label">Preview</div>
            <div className="bubble">{text}</div>
          </div>
        )}
        <div className="field">
          <div className="toggle-row">
            <div className="toggle-copy">
              <strong>Offer human handoff</strong>
              <span>Lets visitors ask for a person when the bot can't help.</span>
            </div>
            <button className={'switch' + (handoff ? ' on' : '')} onClick={onToggle} aria-pressed={handoff}>
              <span className="knob"></span>
            </button>
          </div>
        </div>
        <div className="actions-row">
          <button className="btn-primary" disabled={status === 'saving'} onClick={save}>
            {status === 'saving' && <span className="spinner"></span>}
            {status === 'saving' ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {confirmOff && (
        <ConfirmDialog
          title="Turn off human handoff?"
          body="Visitors won't be able to reach a human when the bot can't help. Continue?"
          confirmLabel="Turn off"
          onConfirm={() => {
            setHandoff(false);
            setConfirmOff(false);
          }}
          onCancel={() => setConfirmOff(false)}
        />
      )}
    </div>
  );
}
